import { useRef, useState, useCallback } from 'react'
import { haptic } from '@/lib/haptics'

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>
  threshold?: number
  maxPull?: number
}

export function usePullToRefresh({
  onRefresh,
  threshold = 60,
  maxPull = 120,
}: UsePullToRefreshOptions) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  // 임계점(release now) 도달 여부 — 인디케이터 시각 전환에 사용
  const [thresholdReached, setThresholdReached] = useState(false)
  const startY = useRef(0)
  const pulling = useRef(false)
  // 임계점 통과 시 햅틱을 한 번만 발생시키기 위한 상태 추적 (release 시 리셋)
  const crossed = useRef(false)

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isRefreshing) return
      // Safari 탭 모드에서는 네이티브 새로고침에 위임 (PWA standalone에서만 커스텀 PTR)
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as unknown as { standalone?: boolean }).standalone === true
      if (!isStandalone) return
      // Use window scroll position (app uses window-level scrolling)
      if (window.scrollY > 0) return
      startY.current = e.touches[0].clientY
      pulling.current = true
    },
    [isRefreshing]
  )

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pulling.current || isRefreshing) return
      const delta = e.touches[0].clientY - startY.current
      if (delta < 0) {
        pulling.current = false
        setPullDistance(0)
        return
      }
      // Dampen the pull (rubber-band feel)
      const dampened = Math.min(maxPull, delta * 0.5)
      setPullDistance(dampened)

      // 임계점 통과 감지: 처음 넘는 순간에만 햅틱 1회 + 'release now' 시각 신호
      const atThreshold = dampened >= threshold
      if (atThreshold && !crossed.current) {
        crossed.current = true
        setThresholdReached(true)
        haptic('light')
      } else if (!atThreshold && crossed.current) {
        crossed.current = false
        setThresholdReached(false)
      }
    },
    [isRefreshing, maxPull, threshold]
  )

  const onTouchEnd = useCallback(async () => {
    if (!pulling.current) return
    pulling.current = false
    // 손을 떼면 임계점 추적 상태 리셋 (다음 당김에서 햅틱 재발생 가능하도록)
    crossed.current = false
    setThresholdReached(false)

    if (pullDistance >= threshold) {
      setIsRefreshing(true)
      setPullDistance(threshold) // hold at threshold
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
  }, [pullDistance, threshold, onRefresh])

  return {
    pullDistance,
    isRefreshing,
    thresholdReached,
    threshold,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  }
}
